/**
 * Toast Notification Utility
 * Replaces browser alert() with styled inline toast notifications.
 */

let activeToast = null;

/**
 * Show a toast notification
 * @param {string} message - The message to display
 * @param {"error"|"success"|"warning"} type - Toast type
 * @param {number} duration - Auto-dismiss in ms (default 5000)
 */
export function showToast(message, type = "error", duration = 5000) {
    // Remove existing toast if any
    if (activeToast) {
        activeToast.remove();
        activeToast = null;
    }

    const icons = {
        error: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`,
        success: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>`,
        warning: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>`,
    };

    const colors = {
        error: { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B", icon: "#DC2626" },
        success: { bg: "#F0FDF4", border: "#86EFAC", text: "#166534", icon: "#16A34A" },
        warning: { bg: "#FFFBEB", border: "#FCD34D", text: "#92400E", icon: "#D97706" },
    };

    const c = colors[type] || colors.error;

    const toast = document.createElement("div");
    toast.className = "ql-toast";
    toast.setAttribute("role", "alert");
    toast.innerHTML = `
    <div style="
      position: fixed;
      top: 24px;
      left: 50%;
      transform: translateX(-50%) translateY(-20px);
      z-index: 99999;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 22px;
      border-radius: 12px;
      background: ${c.bg};
      border: 1px solid ${c.border};
      color: ${c.text};
      font-family: 'Public Sans', system-ui, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 8px 30px rgba(0,0,0,0.12);
      opacity: 0;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      max-width: 90vw;
      line-height: 1.4;
    ">
      <span style="color: ${c.icon}; flex-shrink: 0; display: flex;">${icons[type] || icons.error}</span>
      <span>${message}</span>
      <button onclick="this.closest('.ql-toast').remove()" style="
        background: none;
        border: none;
        color: ${c.text};
        cursor: pointer;
        padding: 0 0 0 8px;
        font-size: 18px;
        line-height: 1;
        opacity: 0.6;
        flex-shrink: 0;
      ">&times;</button>
    </div>
  `;

    document.body.appendChild(toast);
    activeToast = toast;

    // Animate in
    requestAnimationFrame(() => {
        const inner = toast.querySelector("div");
        if (inner) {
            inner.style.opacity = "1";
            inner.style.transform = "translateX(-50%) translateY(0)";
        }
    });

    // Auto dismiss
    if (duration > 0) {
        setTimeout(() => {
            if (toast.parentNode) {
                const inner = toast.querySelector("div");
                if (inner) {
                    inner.style.opacity = "0";
                    inner.style.transform = "translateX(-50%) translateY(-20px)";
                }
                setTimeout(() => toast.remove(), 300);
            }
        }, duration);
    }
}

/**
 * Parse API error and show appropriate toast
 * @param {Response} res - Fetch response object
 */
export async function showApiError(res) {
    try {
        const data = await res.json();
        if (data?.code === 505 || data?.message?.includes("high volume")) {
            showToast(
                "Too many requests. Please wait a moment and try again.",
                "warning",
                6000
            );
        } else if (data?.message) {
            showToast(data.message, "error");
        } else {
            showToast("Submission failed. Please try again.", "error");
        }
    } catch {
        showToast("Submission failed. Please try again.", "error");
    }
}

